import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/queryKeys';
import { DataState } from '@/components/common/DataState';
import {
  adminCostHelpers,
  type CostSummary,
  type CostPipeline,
  type CostPipelineDetail,
  type CostByUser,
  type CostDaily,
  type CostAllSources,
} from '@/lib/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatDuration(ms: number): string {
  if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

const STATUS_COLORS: Record<string, string> = {
  complete: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-800',
  coding: 'bg-blue-100 text-blue-800',
  researching: 'bg-purple-100 text-purple-800',
  designing: 'bg-indigo-100 text-indigo-800',
  optimizing_seo: 'bg-yellow-100 text-yellow-800',
  reviewing: 'bg-teal-100 text-teal-800',
};

const STEP_COLORS: Record<string, string> = {
  research: 'bg-purple-500',
  design: 'bg-indigo-500',
  code: 'bg-blue-500',
  seo: 'bg-yellow-500',
  review: 'bg-teal-500',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<string, string> = {
  generator_pipeline: 'Website Generation',
  editor_chat: 'Editor Chat Refinement',
  blog_generate: 'Blog Generation',
  blog_outline: 'Blog Outline',
  blog_improve: 'Content Improvement',
  blog_seo: 'SEO Suggestions',
  blog_keywords: 'Keyword Suggestions',
  blog_topics: 'Topic Suggestions',
  image_prompt: 'Image Prompt Gen',
  image_dalle: 'DALL-E Image Gen',
  editor_section: 'Section Content Gen',
  chatbot: 'Chatbot',
  embedding: 'Embeddings',
};

type Tab = 'all' | 'overview' | 'pipelines' | 'users' | 'daily';

export default function CostMonitorPage() {
  const [tab, setTab] = useState<Tab>('all');
  // Pipelines pagination + expanded-row state (drives their own queries below).
  const [page, setPage] = useState(1);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  // Each tab lazily fetches its own slice; `enabled` keeps a tab's request from
  // firing until it's the active tab (matching the old effect-per-tab loading).
  const allSourcesQuery = useQuery({
    queryKey: qk.costMonitor('all-sources'),
    queryFn: async () => (await adminCostHelpers.getAllSources()).data.data ?? null,
    enabled: tab === 'all',
  });

  const summaryQuery = useQuery({
    queryKey: qk.costMonitor('summary'),
    queryFn: async () => (await adminCostHelpers.getSummary()).data.data ?? null,
    enabled: tab === 'overview',
  });

  const pipelinesQuery = useQuery({
    queryKey: [...qk.costMonitor('pipelines'), page] as const,
    queryFn: async () => {
      const res = await adminCostHelpers.getPipelines({
        page,
        limit: 20,
        sort: 'createdAt',
        order: 'desc',
      });
      return {
        pipelines: (res.data.data ?? []) as CostPipeline[],
        meta: (res.data.meta as any) ?? { page: 1, totalPages: 1, total: 0 },
      };
    },
    enabled: tab === 'pipelines',
  });

  const jobDetailQuery = useQuery({
    queryKey: [...qk.costMonitor('pipeline-detail'), expandedJob] as const,
    queryFn: async () => (await adminCostHelpers.getPipelineDetail(expandedJob!)).data.data ?? null,
    enabled: tab === 'pipelines' && expandedJob !== null,
  });

  const usersQuery = useQuery({
    queryKey: qk.costMonitor('by-user'),
    queryFn: async () => (await adminCostHelpers.getByUser()).data.data ?? [],
    enabled: tab === 'users',
  });

  const dailyQuery = useQuery({
    queryKey: qk.costMonitor('daily'),
    queryFn: async () => (await adminCostHelpers.getDaily(30)).data.data ?? [],
    enabled: tab === 'daily',
  });

  const toggleJob = (jobId: string) => setExpandedJob((prev) => (prev === jobId ? null : jobId));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Cost Monitor</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track AI token usage and cost across all AI subsystems
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {(['all', 'overview', 'pipelines', 'users', 'daily'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                // Entering the pipelines tab always starts on page 1 (parity
                // with the old effect that called loadPipelines(1) on switch).
                if (t === 'pipelines') setPage(1);
                setTab(t);
              }}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                tab === t
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {t === 'all' && 'All Sources'}
              {t === 'overview' && 'Pipelines Overview'}
              {t === 'pipelines' && 'Pipeline Jobs'}
              {t === 'users' && 'By User'}
              {t === 'daily' && 'Daily Trend'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {tab === 'all' && (
        <DataState query={allSourcesQuery}>
          {(data) => (data ? <AllSourcesTab data={data} /> : null)}
        </DataState>
      )}
      {tab === 'overview' && (
        <DataState query={summaryQuery}>
          {(summary) => (summary ? <OverviewTab summary={summary} /> : null)}
        </DataState>
      )}
      {tab === 'pipelines' && (
        <DataState query={pipelinesQuery}>
          {(resp) => (
            <PipelinesTab
              pipelines={resp.pipelines}
              meta={resp.meta}
              expandedJob={expandedJob}
              jobDetail={jobDetailQuery.data ?? null}
              onPageChange={setPage}
              onToggleJob={toggleJob}
            />
          )}
        </DataState>
      )}
      {tab === 'users' && (
        <DataState query={usersQuery}>{(users) => <UsersTab users={users} />}</DataState>
      )}
      {tab === 'daily' && (
        <DataState query={dailyQuery}>{(data) => <DailyTab data={data} />}</DataState>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// All Sources Tab — Unified view across ALL AI subsystems
// ---------------------------------------------------------------------------

function AllSourcesTab({ data }: { data: CostAllSources }) {
  return (
    <div className="space-y-6">
      {/* Grand Total */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg bg-white p-5 shadow">
          <p className="text-sm font-medium text-gray-500">Total AI Cost (All Sources)</p>
          <p className="mt-1 text-3xl font-bold text-red-600">
            {formatUsd(data.grandTotal.totalCostUsd)}
          </p>
        </div>
        <div className="rounded-lg bg-white p-5 shadow">
          <p className="text-sm font-medium text-gray-500">Total AI Calls</p>
          <p className="mt-1 text-3xl font-bold text-blue-600">
            {data.grandTotal.totalCalls.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Breakdown by Source */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Cost by AI Subsystem</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                  Source
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                  Calls
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                  Cost
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                  Input Tokens
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                  Output Tokens
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                  % of Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.sourceBreakdown.map((row) => (
                <tr key={row.source}>
                  <td className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-900">
                    {SOURCE_LABELS[row.source] || row.source}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-600">
                    {row.totalCalls.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-semibold text-red-600">
                    {formatUsd(row.totalCostUsd)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-500">
                    {formatTokens(row.totalInputTokens)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-500">
                    {formatTokens(row.totalOutputTokens)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-500">
                    {data.grandTotal.totalCostUsd > 0
                      ? ((row.totalCostUsd / data.grandTotal.totalCostUsd) * 100).toFixed(1)
                      : '0'}
                    %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost Distribution Bar */}
      {data.grandTotal.totalCostUsd > 0 && (
        <div className="rounded-lg bg-white p-4 shadow">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Cost Distribution</h3>
          <div className="flex h-6 overflow-hidden rounded-full bg-gray-100">
            {data.sourceBreakdown
              .filter((r) => r.totalCostUsd > 0)
              .map((row, i) => {
                const pct = (row.totalCostUsd / data.grandTotal.totalCostUsd) * 100;
                const colors = [
                  'bg-blue-500',
                  'bg-purple-500',
                  'bg-indigo-500',
                  'bg-green-500',
                  'bg-yellow-500',
                  'bg-red-500',
                  'bg-pink-500',
                  'bg-teal-500',
                  'bg-orange-500',
                ];
                return (
                  <div
                    key={row.source}
                    className={`${colors[i % colors.length]} relative`}
                    style={{ width: `${pct}%` }}
                    title={`${SOURCE_LABELS[row.source] || row.source}: ${formatUsd(row.totalCostUsd)} (${pct.toFixed(1)}%)`}
                  />
                );
              })}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
            {data.sourceBreakdown
              .filter((r) => r.totalCostUsd > 0)
              .map((row, i) => {
                const colors = [
                  'bg-blue-500',
                  'bg-purple-500',
                  'bg-indigo-500',
                  'bg-green-500',
                  'bg-yellow-500',
                  'bg-red-500',
                  'bg-pink-500',
                  'bg-teal-500',
                  'bg-orange-500',
                ];
                return (
                  <span key={row.source} className="flex items-center gap-1">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${colors[i % colors.length]}`}
                    />
                    {SOURCE_LABELS[row.source] || row.source}
                  </span>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({ summary }: { summary: CostSummary }) {
  const s = summary.summary;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Cost"
          value={formatUsd(s.totalCostUsd)}
          sub={`${s.totalJobs} jobs`}
          color="blue"
        />
        <StatCard
          label="Avg Cost / Job"
          value={formatUsd(s.avgCostPerJob)}
          sub={`Range: ${formatUsd(s.minCostJob || 0)} - ${formatUsd(s.maxCostJob || 0)}`}
          color="indigo"
        />
        <StatCard
          label="Input Tokens"
          value={formatTokens(s.totalInputTokens)}
          sub="Total across all jobs"
          color="purple"
        />
        <StatCard
          label="Output Tokens"
          value={formatTokens(s.totalOutputTokens)}
          sub="Total across all jobs"
          color="green"
        />
      </div>

      {/* Job Status Bar */}
      <div className="rounded-lg border bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-700">Job Status Distribution</h3>
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded-full bg-green-500" />
            Complete: {s.completedJobs}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded-full bg-red-500" />
            Errored: {s.erroredJobs}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-3 w-3 rounded-full bg-gray-400" />
            Other: {s.totalJobs - s.completedJobs - s.erroredJobs}
          </span>
        </div>
        {s.totalJobs > 0 && (
          <div className="mt-2 flex h-3 overflow-hidden rounded-full bg-gray-100">
            <div
              className="bg-green-500"
              style={{ width: `${(s.completedJobs / s.totalJobs) * 100}%` }}
            />
            <div
              className="bg-red-500"
              style={{ width: `${(s.erroredJobs / s.totalJobs) * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Step Breakdown Table */}
      <div className="rounded-lg border bg-white">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">Cost by Pipeline Step</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2">Step</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2 text-right">Input Tokens</th>
              <th className="px-4 py-2 text-right">Output Tokens</th>
              <th className="px-4 py-2 text-right">Total Cost</th>
              <th className="px-4 py-2 text-right">Avg Cost</th>
              <th className="px-4 py-2 text-right">Avg Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {summary.stepBreakdown.map((step) => (
              <tr key={step.step} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${STEP_COLORS[step.step] || 'bg-gray-400'}`}
                    />
                    <span className="font-medium capitalize">{step.step}</span>
                  </span>
                </td>
                <td className="px-4 py-2 text-right">{step.totalCalls}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {formatTokens(step.totalInputTokens)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {formatTokens(step.totalOutputTokens)}
                </td>
                <td className="px-4 py-2 text-right font-semibold">
                  {formatUsd(step.totalCostUsd)}
                </td>
                <td className="px-4 py-2 text-right text-gray-500">{formatUsd(step.avgCostUsd)}</td>
                <td className="px-4 py-2 text-right text-gray-500">
                  {formatDuration(step.avgDurationMs)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {summary.stepBreakdown.length === 0 && (
          <p className="px-4 py-6 text-center text-gray-400">No step data available yet</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipelines Tab
// ---------------------------------------------------------------------------

function PipelinesTab({
  pipelines,
  meta,
  expandedJob,
  jobDetail,
  onPageChange,
  onToggleJob,
}: {
  pipelines: CostPipeline[];
  meta: { page: number; totalPages: number; total: number };
  expandedJob: string | null;
  jobDetail: CostPipelineDetail | null;
  onPageChange: (page: number) => void;
  onToggleJob: (jobId: string) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{meta.total} total pipelines</p>

      <div className="space-y-2">
        {pipelines.map((p) => (
          <div key={p.id} className="rounded-lg border bg-white">
            {/* Row */}
            <button
              onClick={() => onToggleJob(p.id)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-gray-100'}`}
                >
                  {p.status}
                </span>
                <span className="truncate text-sm text-gray-700">{p.prompt || '(no prompt)'}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {p.tokenUsage && (
                  <span className="font-semibold text-gray-900">
                    {formatUsd(p.tokenUsage.totalCostUsd)}
                  </span>
                )}
                <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                <svg
                  className={`h-4 w-4 transition-transform ${expandedJob === p.id ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            {/* Expanded Detail */}
            {expandedJob === p.id && jobDetail && (
              <div className="border-t bg-gray-50 px-4 py-4">
                <div className="mb-3 grid grid-cols-2 gap-4 text-xs sm:grid-cols-4">
                  <div>
                    <span className="text-gray-400">Project</span>
                    <p className="font-mono text-gray-700">{jobDetail.projectId}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Created By</span>
                    <p className="font-mono text-gray-700">{jobDetail.createdBy}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Total Tokens</span>
                    <p className="font-mono text-gray-700">
                      {jobDetail.tokenUsage
                        ? `${formatTokens(jobDetail.tokenUsage.totalInputTokens)} in / ${formatTokens(jobDetail.tokenUsage.totalOutputTokens)} out`
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400">Total Cost</span>
                    <p className="font-semibold text-gray-900">
                      {jobDetail.tokenUsage ? formatUsd(jobDetail.tokenUsage.totalCostUsd) : 'N/A'}
                    </p>
                  </div>
                </div>

                {jobDetail.error && (
                  <div className="mb-3 rounded bg-red-50 p-2 text-xs text-red-700">
                    Error: {jobDetail.error.message}
                  </div>
                )}

                {/* Steps Table */}
                {jobDetail.tokenUsage && jobDetail.tokenUsage.steps.length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-1 pr-3">Step</th>
                        <th className="pb-1 pr-3">Model</th>
                        <th className="pb-1 pr-3 text-right">Input</th>
                        <th className="pb-1 pr-3 text-right">Output</th>
                        <th className="pb-1 pr-3 text-right">Cost</th>
                        <th className="pb-1 text-right">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {jobDetail.tokenUsage.steps.map((s, i) => (
                        <tr key={i}>
                          <td className="py-1.5 pr-3">
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className={`h-2 w-2 rounded-full ${STEP_COLORS[s.step] || 'bg-gray-400'}`}
                              />
                              <span className="capitalize">{s.step}</span>
                            </span>
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-gray-500">
                            {s.model.split('-').slice(-2).join('-')}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-mono">
                            {formatTokens(s.inputTokens)}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-mono">
                            {formatTokens(s.outputTokens)}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-semibold">
                            {formatUsd(s.costUsd)}
                          </td>
                          <td className="py-1.5 text-right text-gray-500">
                            {formatDuration(s.durationMs)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Cost bar visualization */}
                {jobDetail.tokenUsage && jobDetail.tokenUsage.steps.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs text-gray-400">Cost distribution</p>
                    <div className="flex h-4 overflow-hidden rounded-full bg-gray-200">
                      {jobDetail.tokenUsage.steps.map((s, i) => {
                        const pct = (s.costUsd / jobDetail.tokenUsage!.totalCostUsd) * 100;
                        return (
                          <div
                            key={i}
                            className={`${STEP_COLORS[s.step] || 'bg-gray-400'}`}
                            style={{ width: `${pct}%` }}
                            title={`${s.step}: ${formatUsd(s.costUsd)} (${pct.toFixed(0)}%)`}
                          />
                        );
                      })}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-500">
                      {jobDetail.tokenUsage.steps.map((s, i) => (
                        <span key={i} className="inline-flex items-center gap-1">
                          <span
                            className={`h-2 w-2 rounded-full ${STEP_COLORS[s.step] || 'bg-gray-400'}`}
                          />
                          {s.step}:{' '}
                          {((s.costUsd / jobDetail.tokenUsage!.totalCostUsd) * 100).toFixed(0)}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {pipelines.length === 0 && (
        <p className="py-12 text-center text-gray-400">No generation pipelines found</p>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <button
            onClick={() => onPageChange(meta.page - 1)}
            disabled={meta.page <= 1}
            className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {meta.page} of {meta.totalPages}
          </span>
          <button
            onClick={() => onPageChange(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            className="rounded border px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users Tab
// ---------------------------------------------------------------------------

function UsersTab({ users }: { users: CostByUser[] }) {
  return (
    <div className="rounded-lg border bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
            <th className="px-4 py-2">User</th>
            <th className="px-4 py-2 text-right">Jobs</th>
            <th className="px-4 py-2 text-right">Completed</th>
            <th className="px-4 py-2 text-right">Input Tokens</th>
            <th className="px-4 py-2 text-right">Output Tokens</th>
            <th className="px-4 py-2 text-right">Total Cost</th>
            <th className="px-4 py-2 text-right">Avg / Job</th>
            <th className="px-4 py-2 text-right">Last Job</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {users.map((u) => (
            <tr key={u.userId} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <div>
                  <p className="font-medium text-gray-900">{u.userName}</p>
                  <p className="text-xs text-gray-400">{u.userEmail}</p>
                </div>
              </td>
              <td className="px-4 py-2 text-right">{u.totalJobs}</td>
              <td className="px-4 py-2 text-right text-green-600">{u.completedJobs}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">
                {formatTokens(u.totalInputTokens)}
              </td>
              <td className="px-4 py-2 text-right font-mono text-xs">
                {formatTokens(u.totalOutputTokens)}
              </td>
              <td className="px-4 py-2 text-right font-semibold">{formatUsd(u.totalCostUsd)}</td>
              <td className="px-4 py-2 text-right text-gray-500">{formatUsd(u.avgCostPerJob)}</td>
              <td className="px-4 py-2 text-right text-xs text-gray-500">
                {new Date(u.lastJobAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && (
        <p className="px-4 py-12 text-center text-gray-400">No user cost data available yet</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily Trend Tab
// ---------------------------------------------------------------------------

function DailyTab({ data }: { data: CostDaily[] }) {
  const maxCost = Math.max(...data.map((d) => d.totalCostUsd), 0.001);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="30-Day Cost"
          value={formatUsd(data.reduce((sum, d) => sum + d.totalCostUsd, 0))}
          sub={`${data.reduce((sum, d) => sum + (d.jobs || d.calls || 0), 0)} jobs`}
          color="blue"
        />
        <StatCard
          label="Avg Daily Cost"
          value={formatUsd(
            data.length ? data.reduce((sum, d) => sum + d.totalCostUsd, 0) / data.length : 0
          )}
          sub={`Across ${data.length} days`}
          color="indigo"
        />
        <StatCard
          label="Peak Day Cost"
          value={formatUsd(maxCost)}
          sub={data.find((d) => d.totalCostUsd === maxCost)?.date || '-'}
          color="red"
        />
      </div>

      {/* Bar chart (pure CSS) */}
      <div className="rounded-lg border bg-white p-4">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">Daily Cost (last 30 days)</h3>
        <div className="flex items-end gap-1" style={{ height: 200 }}>
          {data.map((d) => {
            const pct = (d.totalCostUsd / maxCost) * 100;
            return (
              <div
                key={d.date}
                className="group relative flex flex-1 flex-col items-center justify-end"
                style={{ height: '100%' }}
              >
                <div
                  className="w-full min-w-[4px] rounded-t bg-blue-500 transition-colors hover:bg-blue-600"
                  style={{ height: `${Math.max(pct, 1)}%` }}
                />
                {/* Tooltip */}
                <div className="pointer-events-none absolute bottom-full mb-2 hidden rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                  <p className="font-semibold">{d.date}</p>
                  <p>
                    {formatUsd(d.totalCostUsd)} &middot; {d.jobs} jobs
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {/* X-axis labels (show every 5th date) */}
        <div className="mt-1 flex gap-1 text-[10px] text-gray-400">
          {data.map((d, i) => (
            <div key={d.date} className="flex-1 text-center">
              {i % 5 === 0 ? d.date.slice(5) : ''}
            </div>
          ))}
        </div>
      </div>

      {/* Daily table */}
      <div className="rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2 text-right">Jobs</th>
              <th className="px-4 py-2 text-right">Input Tokens</th>
              <th className="px-4 py-2 text-right">Output Tokens</th>
              <th className="px-4 py-2 text-right">Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[...data].reverse().map((d) => (
              <tr key={d.date} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-gray-700">{d.date}</td>
                <td className="px-4 py-2 text-right">{d.jobs || d.calls || 0}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {formatTokens(d.totalInputTokens || 0)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {formatTokens(d.totalOutputTokens || 0)}
                </td>
                <td className="px-4 py-2 text-right font-semibold">{formatUsd(d.totalCostUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <p className="px-4 py-12 text-center text-gray-400">No daily data available yet</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

const CARD_COLORS: Record<string, string> = {
  blue: 'border-blue-200 bg-blue-50',
  indigo: 'border-indigo-200 bg-indigo-50',
  purple: 'border-purple-200 bg-purple-50',
  green: 'border-green-200 bg-green-50',
  red: 'border-red-200 bg-red-50',
};

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div className={`rounded-lg border p-4 ${CARD_COLORS[color] || 'border-gray-200 bg-gray-50'}`}>
      <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-0.5 text-xs text-gray-500">{sub}</p>
    </div>
  );
}
